import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import StyledButton from '@/components/styled/StyledButton';
import StyledInput from '@/components/styled/StyledInput';
import StyledRadioGroup, {
  YesNoStyledRadioGroup,
} from '@/components/styled/StyledRadioGroup';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '@/components/styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '@/components/styled/StyledTranslatableTextarea';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';

interface CategoryInsuranceListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const CategoryInsuranceList = ({
  control,
  index,
  language,
  isViewMode = false,
}: CategoryInsuranceListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `carCategories.${index}.insurances`,
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: `carCategories.${index}.insurances`,
    });

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <p className="text-sm leading-6 font-medium text-gray-700">
          {t('carRental.categoryForm.limitInsuranceQuestion')}
        </p>
        <YesNoStyledRadioGroup
          control={control}
          name={`carCategories.${index}.shouldLimitInsurance`}
          disabled={isViewMode}
        />
      </div>
      {fields.map((item, insuranceIndex) => (
        <CategoryInsuranceCard
          key={item.id}
          control={control}
          index={index}
          insuranceIndex={insuranceIndex}
          language={language}
          deleteInsurance={() => {
            remove(insuranceIndex);
            removeTranslation(insuranceIndex);
          }}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addInsurance')}
          onClick={() => {
            append({
              name: undefined,
              shortDescription: undefined,
              longDescription: undefined,
              amount: undefined,
              billingType: undefined,
            } as CarRentalFormDataWithTranslations['carCategories'][number]['insurances'][number]);
            appendTranslation(() => ({
              name: undefined,
              shortDescription: undefined,
              longDescription: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default CategoryInsuranceList;

interface CategoryInsuranceCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  insuranceIndex: number;
  language: SupportedLocale;
  deleteInsurance: () => void;
  isViewMode?: boolean;
}

const CategoryInsuranceCard = ({
  control,
  index,
  insuranceIndex,
  language,
  deleteInsurance,
  isViewMode = false,
}: CategoryInsuranceCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `carCategories.${index}.insurances.${insuranceIndex}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation =
      translations?.[language]?.carCategories?.[index]?.insurances?.[
        insuranceIndex
      ];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.insurance', { index: insuranceIndex + 1 })
    );
  }, [currentTitle, index, insuranceIndex, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {insuranceIndex > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteInsurance()}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            originalName={`carCategories.${index}.insurances.${insuranceIndex}.name`}
            control={control}
            title={t('carRental.categoryForm.name')}
            name={`carCategories.${index}.insurances.${insuranceIndex}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.namePlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableInput
            originalName={`carCategories.${index}.insurances.${insuranceIndex}.shortDescription`}
            control={control}
            title={t('carRental.categoryForm.shortDescription')}
            name={`carCategories.${index}.insurances.${insuranceIndex}.shortDescription`}
            setValue={setValue}
            language={language}
            placeholder={t(
              'carRental.categoryForm.shortDescriptionPlaceholder',
            )}
            disabled={isViewMode}
          />
          <StyledTranslatableTextarea
            control={control}
            originalName={`carCategories.${index}.insurances.${insuranceIndex}.longDescription`}
            title={t('carRental.categoryForm.longDescription')}
            name={`carCategories.${index}.insurances.${insuranceIndex}.longDescription`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.longDescriptionPlaceholder')}
            rows={3}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex gap-6 w-full items-center">
              <div className="min-w-20 max-w-40">
                <StyledInput
                  control={control}
                  name={`carCategories.${index}.insurances.${insuranceIndex}.amount`}
                  placeholder={t('carRental.categoryForm.amountPlaceholder')}
                  type="number"
                  min={0}
                  disabled={isViewMode}
                />
              </div>
              <StyledRadioGroup
                control={control}
                name={`carCategories.${index}.insurances.${insuranceIndex}.billingType`}
                options={[
                  {
                    label: t('carRental.categoryForm.billingTypePerDay'),
                    value: 'perDay',
                  },
                  {
                    label: t('carRental.categoryForm.billingTypePerRental'),
                    value: 'perRental',
                  },
                ]}
                className="w-1/2"
                disabled={isViewMode}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
